import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon.jsx';
import { formatCurrency, formatNumber, generateShareText, generateCSVData } from '../utils/calculations.js';
import { copyToClipboard, downloadCSV, downloadPDF } from '../utils/export.js';

const { FiBarChart3, FiToggleLeft, FiToggleRight, FiCopy, FiDownload, FiFileText, FiCheck } = FiIcons;

const Results = ({ inputs, results }) => {
  const [showAllIn, setShowAllIn] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  if (!inputs || !results) return null;

  const handleCopyResults = async () => {
    const shareText = generateShareText(results, showAllIn);
    const success = await copyToClipboard(shareText);
    
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleDownloadCSV = () => {
    const timestamp = new Date().toISOString();
    const csvData = generateCSVData(inputs, results, timestamp);
    downloadCSV(csvData);
  };

  const handleDownloadPDF = () => {
    downloadPDF(inputs, results);
  };

  const resultTiles = [
    {
      label: 'Monthly Diesel Spend',
      value: formatCurrency(results.monthlySpend),
      unit: '',
      color: 'bg-blue-50 text-blue-700 border-blue-200'
    },
    {
      label: 'Estimated kWh Generated',
      value: formatNumber(results.kWhGenerated),
      unit: 'kWh',
      color: 'bg-green-50 text-green-700 border-green-200'
    },
    {
      label: 'Fuel-only Cost per kWh',
      value: formatCurrency(results.fuelCostPerKwh, 3),
      unit: '',
      color: 'bg-violet-50 text-violet-700 border-violet-200'
    }
  ];

  return (
    <section className="py-16 px-4 bg-white">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-3 mb-8">
            <SafeIcon 
              icon={FiBarChart3} 
              className="text-2xl text-violet-600" 
              aria-hidden="true"
            />
            <h2 className="text-2xl font-bold text-gray-900">
              Your Results
            </h2>
          </div>

          {/* Result Tiles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {resultTiles.map((tile, index) => (
              <motion.div
                key={tile.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className={`p-6 rounded-lg border-2 ${tile.color}`}
              >
                <h3 className="text-sm font-medium mb-2">{tile.label}</h3>
                <div className="text-2xl font-bold">
                  {tile.value}
                  {tile.unit && <span className="text-lg font-normal ml-1">{tile.unit}</span>}
                </div>
              </motion.div>
            ))}
          </div>

          {/* All-in Estimate Toggle */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  All-in Cost Estimate
                </h3>
                <p className="text-sm text-gray-600">
                  Adds 20% to 40% for maintenance, oil, filters, repairs, and staff time
                </p>
              </div>
              <button
                onClick={() => setShowAllIn(!showAllIn)}
                className="flex items-center gap-2 text-violet-600 hover:text-violet-700 transition-colors focus:outline-none focus:ring-2 focus:ring-violet-200 rounded-lg p-2"
                aria-label={showAllIn ? 'Hide all-in estimate' : 'Show all-in estimate'}
              >
                <SafeIcon 
                  icon={showAllIn ? FiToggleRight : FiToggleLeft} 
                  className="text-2xl" 
                  aria-hidden="true"
                />
                <span className="font-medium">
                  {showAllIn ? 'Hide' : 'Show'}
                </span>
              </button>
            </div>

            {showAllIn && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="border-t border-gray-200 pt-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h4 className="text-sm font-medium text-gray-600 mb-1">Low Estimate (+20%)</h4>
                    <div className="text-xl font-bold text-gray-900">
                      {formatCurrency(results.allInLow, 3)}
                      <span className="text-sm font-normal ml-1">per kWh</span>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h4 className="text-sm font-medium text-gray-600 mb-1">High Estimate (+40%)</h4>
                    <div className="text-xl font-bold text-gray-900">
                      {formatCurrency(results.allInHigh, 3)}
                      <span className="text-sm font-normal ml-1">per kWh</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Share and Export */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Share & Export
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <motion.button
                onClick={handleCopyResults}
                className="flex items-center justify-center gap-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 px-4 py-3 rounded-lg font-medium transition-colors focus:outline-none focus:ring-4 focus:ring-gray-200"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon 
                  icon={copySuccess ? FiCheck : FiCopy} 
                  className={copySuccess ? 'text-green-600' : ''} 
                  aria-hidden="true"
                />
                {copySuccess ? 'Copied!' : 'Copy Summary'}
              </motion.button>

              <motion.button
                onClick={handleDownloadCSV}
                className="flex items-center justify-center gap-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 px-4 py-3 rounded-lg font-medium transition-colors focus:outline-none focus:ring-4 focus:ring-gray-200"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiDownload} aria-hidden="true" />
                Export CSV
              </motion.button>

              <motion.button
                onClick={handleDownloadPDF}
                className="flex items-center justify-center gap-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 px-4 py-3 rounded-lg font-medium transition-colors focus:outline-none focus:ring-4 focus:ring-gray-200"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiFileText} aria-hidden="true" />
                Download PDF
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Results;